import {
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
  useHover,
  useInteractions,
  useMergeRefs,
  FloatingPortal,
  type Placement,
  safePolygon,
} from '@floating-ui/react';
import { cloneElement, forwardRef, isValidElement, useState, type ReactElement, type ReactNode } from 'react';

interface TooltipProps {
  children: ReactElement;
  label: ReactNode;
  placement?: Placement;
  shortcut?: string;
  delay?: number;
}

type AnyHandler = ((...args: unknown[]) => void) | undefined;

/**
 * Hover tooltip. Forwards its ref and any extra props (e.g. the click handler and
 * aria attributes a wrapping <Popover> injects) to the child element.
 */
export const Tooltip = forwardRef<HTMLElement, TooltipProps>(function Tooltip(props, forwardedRef) {
  const { children, label, placement = 'bottom', shortcut, delay = 350, ...restProps } = props;
  // extra props arrive untyped via cloneElement from wrappers such as <Popover>
  const rest = restProps as Record<string, unknown>;
  const [open, setOpen] = useState(false);
  const { refs, floatingStyles, context, isPositioned } = useFloating({
    open,
    onOpenChange: setOpen,
    placement,
    whileElementsMounted: autoUpdate,
    middleware: [offset(6), flip(), shift({ padding: 6 })],
  });
  const hover = useHover(context, { delay: { open: delay, close: 0 }, handleClose: safePolygon({ blockPointerEvents: false }) });
  const { getReferenceProps, getFloatingProps } = useInteractions([hover]);
  const childRef = isValidElement(children) ? (children as unknown as { ref?: React.Ref<HTMLElement> }).ref : undefined;
  const ref = useMergeRefs([refs.setReference, forwardedRef, childRef]);
  if (!isValidElement(children)) return children;

  const childProps = children.props as Record<string, unknown>;
  // chain event handlers present on both the injected props and the child
  const merged: Record<string, unknown> = { ...childProps, ...rest };
  for (const key of Object.keys(rest)) {
    if (/^on[A-Z]/.test(key) && typeof childProps[key] === 'function' && typeof rest[key] === 'function') {
      const a = rest[key] as AnyHandler;
      const b = childProps[key] as AnyHandler;
      merged[key] = (...args: unknown[]) => {
        b?.(...args);
        a?.(...args);
      };
    }
  }
  if (!label) return cloneElement(children as ReactElement<Record<string, unknown>>, { ...merged, ref });
  return (
    <>
      {cloneElement(children as ReactElement<Record<string, unknown>>, { ...getReferenceProps(merged), ref })}
      {open && (
        <FloatingPortal>
          <div ref={refs.setFloating} style={floatingStyles} className="tooltip" data-floating="" data-positioned={isPositioned ? '' : undefined} {...getFloatingProps()}>
            {label}
            {shortcut && (
              <span className="tooltip-kbd">
                {shortcut.split(' ').map((k) => (
                  <kbd key={k}>{k}</kbd>
                ))}
              </span>
            )}
          </div>
        </FloatingPortal>
      )}
    </>
  );
});
